/**
 * Patient Types and Interfaces
 *
 * Type definitions for patient records, demographics, and related data structures.
 */

/**
 * Gender options - includes 'U' for Unknown to support external systems/legacy data
 * Note: Forms should only expose M, F, O options to users
 */
export type Gender = 'M' | 'F' | 'O' | 'U'; // Male, Female, Other, Unknown

/**
 * Gender options for form input - excludes Unknown as it's not user-selectable
 */
export type FormGender = 'M' | 'F' | 'O'; // Male, Female, Other

/**
 * Marital Status options following FHIR standard
 * @see https://www.medplum.com/docs/api/fhir/resources/patient (maritalStatus field)
 * @see http://hl7.org/fhir/R4/valueset-marital-status.html
 */
export type MaritalStatus = 'S' | 'M' | 'D' | 'W' | 'U'; // Single, Married, Divorced, Widowed, Unknown

/**
 * Marital Status display labels
 */
export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  S: 'Single (Never Married)',
  M: 'Married',
  D: 'Divorced',
  W: 'Widowed',
  U: 'Unknown',
};

/**
 * Marital Status options for forms (excludes Unknown as it's auto-selected default)
 */
export type FormMaritalStatus = 'S' | 'M' | 'D' | 'W';

/**
 * Marital Status labels for form display
 */
export const FORM_MARITAL_STATUS_LABELS: Record<FormMaritalStatus, string> = {
  S: 'Single',
  M: 'Married',
  D: 'Divorced',
  W: 'Widowed',
};

/**
 * Patient status
 */
export type PatientStatus = 'active' | 'inactive' | 'deceased';

/**
 * Patient Demographics
 */
export interface PatientDemographics {
  /**
   * Patient UUID (OpenMRS format)
   */
  uuid: string;

  /**
   * Patient identifier/MRN
   */
  identifier: string;

  /**
   * Full name
   */
  name: string;

  /**
   * First name
   */
  firstName?: string;

  /**
   * Last name
   */
  lastName?: string;

  /**
   * Date of birth (ISO 8601 format)
   */
  dateOfBirth: string;

  /**
   * Age in years (calculated)
   */
  age: number;

  /**
   * Gender
   */
  gender: Gender;

  /**
   * Marital Status (FHIR standard)
   */
  maritalStatus?: MaritalStatus;

  /**
   * Contact phone number
   */
  phoneNumber?: string;

  /**
   * Email address
   */
  email?: string;

  /**
   * Residential address
   */
  address?: string;

  /**
   * City
   */
  city?: string;

  /**
   * State/Province
   */
  state?: string;

  /**
   * Postal/ZIP code
   */
  postalCode?: string;

  /**
   * Country
   */
  country?: string;

  /**
   * Emergency contact name
   */
  emergencyContactName?: string;

  /**
   * Emergency contact phone
   */
  emergencyContactPhone?: string;

  /**
   * Patient status
   */
  status: PatientStatus;

  /**
   * Date created (ISO 8601 format)
   */
  createdAt: string;

  /**
   * Date last updated (ISO 8601 format)
   */
  updatedAt: string;

  /**
   * AI-generated visit summary from Patient.extension
   */
  visitSummary?: string;

  /**
   * Last updated timestamp for visit summary
   */
  visitSummaryLastUpdated?: string;
}

/**
 * Patient for list display (minimal info)
 */
export interface PatientListItem {
  uuid: string;
  identifier: string;
  name: string;
  age: number;
  gender: Gender;
  phoneNumber?: string;
  status: PatientStatus;
  lastUpdated?: string;
}

/**
 * Patient registration form data
 */
export interface PatientRegistrationData {
  identifier?: string; // Optional - may be auto-generated
  firstName: string;
  lastName: string;
  dateOfBirth?: string; // Optional - can be calculated from age
  gender: Gender;
  maritalStatus?: MaritalStatus;
  phoneNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  /** Organization reference (id) */
  organizationId?: string;
  /** Organization display name */
  organizationName?: string;
}

/**
 * Patient update data (partial updates allowed)
 */
export type PatientUpdateData = Partial<
  Omit<PatientDemographics, 'uuid' | 'createdAt' | 'updatedAt'>
>;

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number;
  limit?: number;
}

/**
 * Patient search parameters
 */
export interface PatientSearchParams extends PaginationParams {
  /**
   * Search query (name, identifier, phone)
   */
  query?: string;

  /**
   * Filter by gender
   */
  gender?: Gender;

  /**
   * Filter by status
   */
  status?: PatientStatus;

  /**
   * Sort field
   */
  sortBy?: 'name' | 'age' | 'createdAt' | 'lastVisitDate';

  /**
   * Sort order
   */
  sortOrder?: 'asc' | 'desc';

  /**
   * Filter by age range
   */
  minAge?: number;
  maxAge?: number;

  /**
   * Filter by specific patient IDs (used for practitioner-scoped views)
   */
  patientIds?: string[];
}

/**
 * Paginated patient list response
 */
export interface PaginatedPatientsResponse {
  /**
   * Array of patients
   */
  patients: PatientListItem[];

  /**
   * Pagination metadata
   */
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Patient detail response
 */
export interface PatientDetailResponse {
  patient: PatientDemographics;
  // Additional data like visits, consultations will be added in later phases
}

/**
 * Someone else sharing a phone number with the patient being registered.
 * Surfaced by `POST /v2/patients` when it declines to create the chart yet
 * (`needsReview: true`) — the front desk picks one to link to, or confirms
 * "none of these" to create a fresh record. Demographics + org list only —
 * never clinical content.
 */
export interface PersonCandidate {
  personId: string;
  name?: string;
  gender?: string;
  birthDate?: string;
  organizations: Array<{
    patientId: string;
    reference?: string;
    display?: string;
    assurance?: string;
  }>;
}

/**
 * Patient update response
 */
export interface PatientUpdateResponse {
  patient: PatientDemographics;
  message: string;
}

/**
 * Patient deletion response
 */
export interface PatientDeletionResponse {
  message: string;
  deletedUuid: string;
}

/**
 * Gender display labels - includes all possible values
 */
export const GENDER_LABELS: Record<Gender, string> = {
  M: 'Male',
  F: 'Female',
  O: 'Other',
  U: 'Unknown',
};

/**
 * Form gender labels - only user-selectable options (excludes Unknown)
 */
export const FORM_GENDER_LABELS: Record<FormGender, string> = {
  M: 'Male',
  F: 'Female',
  O: 'Other',
};

/**
 * Patient status display labels
 */
export const STATUS_LABELS: Record<PatientStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  deceased: 'Deceased',
};

/**
 * Patient status badge variants
 */
export const STATUS_VARIANTS: Record<
  PatientStatus,
  'default' | 'success' | 'warning' | 'destructive'
> = {
  active: 'success',
  inactive: 'warning',
  deceased: 'destructive',
};

/**
 * Helper to calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string): number {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Helper to calculate approximate date of birth from age
 * Returns January 1st of the calculated birth year (YYYY-MM-DD format)
 * This is used when patient doesn't know their exact birth date
 */
export function calculateDobFromAge(age: number): string {
  const today = new Date();
  const birthYear = today.getFullYear() - age;
  // Use January 1st as the default day when only age is known
  return `${birthYear}-01-01`;
}

/**
 * Helper to format patient name
 */
export function formatPatientName(patient: PatientListItem | PatientDemographics): string {
  return patient.name || 'Unknown Patient';
}

/**
 * Helper to format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Helper to format date of birth with age
 */
export function formatDateOfBirthWithAge(dateOfBirth: string, age: number): string {
  return `${formatDate(dateOfBirth)} (${age} years)`;
}

/**
 * Helper to get gender display label
 */
export function getGenderLabel(gender: Gender): string {
  return GENDER_LABELS[gender];
}

/**
 * Helper to get status display label
 */
export function getStatusLabel(status: PatientStatus): string {
  return STATUS_LABELS[status];
}

/**
 * Helper to get status badge variant
 */
export function getStatusVariant(status: PatientStatus) {
  return STATUS_VARIANTS[status];
}

/**
 * Helper to format gender from FHIR gender string or single-char code
 * Handles both formats:
 * - FHIR: 'male', 'female', 'other', 'unknown'
 * - Single-char: 'M', 'F', 'O', 'U'
 * @param gender - Gender string in either format
 * @returns Formatted gender display string
 */
export function formatGenderDisplay(gender: string | undefined): 'Male' | 'Female' | 'Other' {
  if (!gender) return 'Other';
  const g = gender.toLowerCase();
  // Handle single-char codes (M/F/O/U)
  if (g === 'm') return 'Male';
  if (g === 'f') return 'Female';
  // Handle FHIR format (male/female/other/unknown)
  if (g === 'male') return 'Male';
  if (g === 'female') return 'Female';
  return 'Other';
}

/**
 * Helper to get phone number from FHIR telecom array
 * @param telecom - FHIR ContactPoint array
 * @returns Phone number string or 'N/A'
 */
export function getPhoneFromTelecom(
  telecom: Array<{ system?: string; value?: string }> | undefined
): string {
  if (!telecom || telecom.length === 0) return 'N/A';
  const phone = telecom.find((t) => t.system === 'phone');
  return phone?.value || telecom[0]?.value || 'N/A';
}

/**
 * Helper to get patient name from FHIR Patient resource name array
 * @param name - FHIR HumanName array
 * @returns Formatted patient name or 'Unknown Patient'
 */
export function getPatientNameFromFhir(
  name: Array<{ text?: string; given?: string[]; family?: string }> | undefined
): string {
  if (!name || name.length === 0) return 'Unknown Patient';
  const primaryName = name[0];
  const given = primaryName.given?.join(' ') || '';
  const family = primaryName.family || '';
  const computed = `${given} ${family}`.trim();
  return computed || primaryName.text || 'Unknown Patient';
}
